package dev.lingvo.shared.agent

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonPrimitive

/**
 * The MCP clients Lingvo has a setup story for, named once.
 *
 * Client ids, configuration paths and snippets are shared by CLI and website setup.
 *
 * The snippets are literals and interpolate nothing. On the website that is load-bearing — the
 * config is the one thing on that page somebody pastes without reading — and the CLI's slug
 * substitution happens outside them, from the local `lingvo.yaml` the user wrote themselves.
 */
@Serializable
enum class AgentClient(
    val id: String,
    val displayName: String,
    /**
     * Where the config belongs in the customer's repository, or null when it does not live there.
     *
     * Claude Desktop reads one global file, and CI reads whatever the pipeline gives it. Writing
     * either into a repository would put a file somewhere nothing looks, so those two are printed
     * with instructions instead.
     */
    val repositoryConfigPath: String?,
) {
    CLAUDE_CODE("claude", "Claude Code", ".mcp.json"),
    CODEX("codex", "Codex", ".codex/config.toml"),
    CLAUDE_DESKTOP("claude-desktop", "Claude Desktop", null),
    CURSOR("cursor", "Cursor", ".cursor/mcp.json"),
    CI("ci", "CI", null),
    ;

    companion object {
        fun byId(id: String): AgentClient? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }

        val IDS: List<String> get() = entries.map { it.id }
    }
}

/**
 * The snippets themselves.
 *
 * **The two Claude snippets are not identical and neither are the two remote ones.** Claude
 * Desktop's HTTP form carries `"type": "http"` and Cursor's does not, which is the kind of
 * difference that gets lost the moment somebody writes "the JSON config" once and reuses it.
 */
object AgentClients {

    /** The default remote endpoint, so nothing has to remember the path. */
    const val REMOTE_URL = "https://api.lingvo.dev/api/v1/mcp"

    /** File contents differ from the shell setup instructions shown on the website. */
    fun repositoryConfig(client: AgentClient, remote: Boolean): String {
        require(client.repositoryConfigPath != null) { "${client.id} has no repository config" }
        if (client != AgentClient.CLAUDE_CODE) return if (remote) remote(client) else local(client)
        return if (remote) {
            """
                {
                  "mcpServers": {
                    "lingvo": {
                      "type": "http",
                      "url": "$REMOTE_URL",
                      "headers": { "Authorization": "Bearer ${'$'}{LINGVO_AGENT_TOKEN}" }
                    }
                  }
                }
            """.trimIndent()
        } else {
            local(AgentClient.CURSOR)
        }
    }

    /**
     * Running as the developer, from their own repository.
     *
     * @param workingDirectory named only for Claude Desktop, which has no notion of the repository
     *   it is working in — every other client is launched inside one.
     */
    fun local(client: AgentClient, workingDirectory: String? = null): String = when (client) {
        AgentClient.CODEX -> """
            [mcp_servers.lingvo]
            command = "lingvo"
            args = ["mcp"]
        """.trimIndent()
        AgentClient.CLAUDE_CODE -> "claude mcp add lingvo -- lingvo mcp"
        AgentClient.CLAUDE_DESKTOP -> """
            {
              "mcpServers": {
                "lingvo": {
                  "command": "lingvo",
                  "args": ["mcp"],
                  "cwd": ${JsonPrimitive(workingDirectory ?: "/path/to/your/repo").toString()}
                }
              }
            }
        """.trimIndent()
        AgentClient.CURSOR -> """
            {
              "mcpServers": {
                "lingvo": { "command": "lingvo", "args": ["mcp"] }
              }
            }
        """.trimIndent()
        AgentClient.CI -> """
            # The local server runs as whoever holds the token, so CI needs one with the narrow
            # scopes the job actually uses — not a personal credential.
            export LINGVO_TOKEN="${'$'}{{ secrets.LINGVO_TOKEN }}"
            lingvo qa --fail-on-error
        """.trimIndent()
    }

    /** Running as an agent connection, under its scopes, autonomy and budget. */
    fun remote(client: AgentClient): String = when (client) {
        AgentClient.CODEX -> """
            [mcp_servers.lingvo]
            url = "$REMOTE_URL"
            bearer_token_env_var = "LINGVO_AGENT_TOKEN"
        """.trimIndent()
        AgentClient.CLAUDE_CODE ->
            "claude mcp add --transport http lingvo $REMOTE_URL \\\n" +
                "  --header \"Authorization: Bearer \$LINGVO_AGENT_TOKEN\""
        // The only one that carries a type. Cursor infers it from the presence of a url.
        AgentClient.CLAUDE_DESKTOP -> """
            {
              "mcpServers": {
                "lingvo": {
                  "type": "http",
                  "url": "$REMOTE_URL",
                  "headers": { "Authorization": "Bearer ${'$'}LINGVO_AGENT_TOKEN" }
                }
              }
            }
        """.trimIndent()
        AgentClient.CURSOR -> """
            {
              "mcpServers": {
                "lingvo": {
                  "url": "$REMOTE_URL",
                  "headers": { "Authorization": "Bearer ${'$'}{env:LINGVO_AGENT_TOKEN}" }
                }
              }
            }
        """.trimIndent()
        AgentClient.CI -> """
            # An agent connection, scoped and budgeted in Settings → Agents.
            export LINGVO_AGENT_TOKEN="${'$'}{{ secrets.LINGVO_AGENT_TOKEN }}"
            curl -s $REMOTE_URL \
              -H "Authorization: Bearer ${'$'}LINGVO_AGENT_TOKEN" \
              -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'
        """.trimIndent()
    }
}
